import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import planeIcon from '../../assets/plane-icon.svg';
import dropdownIcon from '../assets/icon-dropdown.svg';
import { Aircraft } from '../../models/aircraft';
import StatusBadge from '../../components/Status/StatusBadge';
import useGetOperatorSetting from '../../utils/useGetOperatorSetting';

interface AircraftDropdownButtonProps {
  handleDropdownClick: () => void;
  dropdownVisible?: boolean;
  selectedAircraft: Aircraft;
}

const StyledButton = styled.button`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: transparent;
  border: none;
  padding: 0;
  color: ${({ theme }): string => theme.colors.white};
`;

const PlaneWrapper = styled.div`
  height: 42px;
  display: flex;
  align-items: center;
`;

const PlaneIcon = styled.img`
  width: 30px;
  margin-right: 16px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const RegWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  span {
    font-size: 16px;
    font-weight: 600;
    flex-shrink: 0;
  }
`;

const StatusText = styled.span`
  font-size: 12px;
  font-weight: 300;
  opacity: 0.7;
  text-transform: capitalize;
`;

const StyledChevron = styled.img`
  width: 10px;
  margin: 8px 0 0 4px;
  transform: ${({ dropdownVisible }): string => (dropdownVisible ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.3s ease;
`;

const getAircraftStatus = (aircraft, formatMessage, releaseText) => {
  let acStatus = 'unknown';
  let acStatusText = formatMessage({ id: 'status.unknown' });

  if (aircraft && aircraft.maintenance_status) {
    if (aircraft.maintenance_status === 'ok' || aircraft.maintenance_status === 'serviceable') {
      acStatus = 'success';
      acStatusText = formatMessage({ id: 'status.serviceable' });
    } else if (aircraft.maintenance_status === 'critical') {
      acStatus = 'warning';
      acStatusText = formatMessage({ id: 'status.inTolerance' });
    } else if (aircraft.maintenance_status === 'overdue') {
      acStatus = 'error';
      acStatusText = formatMessage({ id: 'status.overdue' });
    } else if (aircraft.maintenance_status === 'unserviceable') {
      acStatus = 'error';
      if (aircraft.maintenance_status_reason === 'missing_crs') {
        acStatusText = formatMessage({ id: 'status.unserviceable_missing_crs_short' }, { releaseText });
      } else {
        acStatusText = formatMessage({ id: 'status.unserviceable' });
      }
    }
  }

  return {
    status: acStatus,
    text: acStatusText,
  };
};

const AircraftDropdownButton: React.FC<AircraftDropdownButtonProps> = ({
  handleDropdownClick,
  dropdownVisible,
  selectedAircraft
}) => {
  const { formatMessage } = useIntl();
  const crsShortName = useGetOperatorSetting('crs_short_name', selectedAircraft?.id);
  const approvalReleaseMode = useGetOperatorSetting('approval_release_mode', selectedAircraft?.id);
  const approvalReleaseName = useGetOperatorSetting('approval_release_name', selectedAircraft?.id);

  const releaseText = [
    'after_approval_release_required',
    'after_each_approval_release_required'
  ].includes(approvalReleaseMode) ? approvalReleaseName : crsShortName;

  const aircraftStatus = getAircraftStatus(selectedAircraft, formatMessage, releaseText);

  return (
    <StyledButton onClick={handleDropdownClick} data-testid="AircraftDropdownNew--DropdownButton">
      <PlaneWrapper data-testid="AircraftDropdownNew--PlaneWrapper">
        <PlaneIcon src={planeIcon} alt="planeIcon" data-testid="AircraftDropdownNew--PlaneIcon" />
      </PlaneWrapper>
      <TextWrapper data-testid="AircraftDropdownNew--TextWrapper">
        <RegWrapper data-testid="AircraftDropdownNew--RegWrapper">
          <StatusBadge status={aircraftStatus.status} width="3.5" dataTestId="AircraftDropdownNew--StatusIcon" />
          <span data-testid="AircraftDropdownNew--Registration">{selectedAircraft?.registration}</span>
        </RegWrapper>
        <StatusText data-testid="AircraftDropdownNew--StatusText">
          {aircraftStatus.text}
        </StatusText>
      </TextWrapper>
      <StyledChevron src={dropdownIcon} alt="dropdownIcon" dropdownVisible={dropdownVisible} />
    </StyledButton>
  )
};

AircraftDropdownButton.defaultProps = {
  dropdownVisible: false
};

export default AircraftDropdownButton;
